import { Markdown } from '@cadstrom/sigma-engine-shared';
import { $nodeSchema, $view } from '@milkdown/kit/utils';

import { getNetSearchMatches, getNetSearchTerm } from '#components/cad-renderer/model-reference-search.js';
import {
	createReferenceChipNodeView,
	createReferenceSuggestion,
	getDirectiveReferenceValue,
	isInlineReferenceDirectiveNode,
	isReferenceDirectiveNode,
} from './markdown-editor-reference-plugin.js';
import { BasicRemarkPlugin } from './markdown-plugins-utils.js';
import { NetMarkdownItem } from './net-reference-markdown-item.js';

import type { ModelWithNetStats } from '@cadstrom/sigma-engine-shared';
import type { EditorView } from '@milkdown/kit/prose/view';
import type {
	MarkdownReferenceEditorPlugin,
	MarkdownReferenceSuggestion,
	ReferenceChipViewController,
	ReferenceSuggestionTrigger,
} from './markdown-editor-reference-plugin.js';
import type { RemarkMarkdownDirectiveDefinition } from './markdown-plugins-utils.js';

const NET_REFERENCE_SHORTHAND_PATTERN = /#([A-Za-z0-9_/]+(?:[A-Za-z0-9_./:+-]*[A-Za-z0-9_+:-])?)/g;

const NET_REFERENCE_SHORTHAND_VALUE_PATTERN = /^[A-Za-z0-9_/]+(?:[A-Za-z0-9_./:+-]*[A-Za-z0-9_+:-])?$/;

export const NET_REFERENCE_NODE_NAME = 'netReference';

/**
 * Returns true when the given value can be written using the `#NetA` shorthand form.
 */
export function isValidNetReferenceShorthand(value: string) {
	return NET_REFERENCE_SHORTHAND_VALUE_PATTERN.test(value);
}

/**
 * Formats a net name as markdown, preferring the `#` shorthand and falling back to a directive.
 */
export function formatNetReferenceMarkdown(netName: string) {
	return isValidNetReferenceShorthand(netName) ?
			`${Markdown.NetReferenceMarkdownDirectiveDefinition.editorMarker}${netName}`
		:	`:${Markdown.NetReferenceMarkdownDirectiveDefinition.directive}[${escapeDirectiveContent(netName)}]`;
}

function escapeDirectiveContent(value: string) {
	return value.replace(/([\\[\]])/g, '\\$1');
}

/**
 * Remark plugin definition for net reference directives
 */
export const netMarkdownItemDefinition: RemarkMarkdownDirectiveDefinition<
	typeof Markdown.NetReferenceMarkdownDirectiveDefinition
> = {
	allowedAttributes: Markdown.NetReferenceMarkdownDirectiveDefinition.allowedAttributes,
	directive: BasicRemarkPlugin(
		Markdown.NetReferenceMarkdownDirectiveDefinition.directive,
		Markdown.NodeTypes.AllDirectives,
		{
			contentPropName: Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName,
			shorthandPattern: NET_REFERENCE_SHORTHAND_PATTERN,
		},
	),
	item: NetMarkdownItem,
};

/**
 * Milkdown schema for inline net reference atoms.
 */
export const netReferenceSchema = $nodeSchema(NET_REFERENCE_NODE_NAME, () => ({
	inline: true,
	group: 'inline',
	atom: true,
	selectable: true,
	draggable: false,
	marks: '',
	attrs: {
		[Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName]: { default: '', validate: 'string' },
	},
	parseDOM: [
		{
			tag: `span[data-markdown-reference-kind="${Markdown.NetReferenceMarkdownDirectiveDefinition.directive}"]`,
			getAttrs: (dom) => {
				if (!(dom instanceof HTMLElement)) {
					return false;
				}
				return {
					[Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName]:
						dom.getAttribute(`data-${Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName}`) ?? '',
				};
			},
		},
	],
	toDOM: (node) => {
		const netName = String(node.attrs[Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName]);
		return [
			'span',
			{
				'data-markdown-reference-kind': Markdown.NetReferenceMarkdownDirectiveDefinition.directive,
				[`data-${Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName}`]: netName,
				class: 'inline-flex align-baseline',
			},
			netName,
		];
	},
	leafText: (node) =>
		formatNetReferenceMarkdown(
			String(node.attrs[Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName]),
		),
	parseMarkdown: {
		match: (node) => isReferenceDirectiveNode(node, Markdown.NetReferenceMarkdownDirectiveDefinition.directive),
		runner: (state, node, type) => {
			const netName = getDirectiveReferenceValue(
				node,
				Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName,
			);
			if (netName === undefined) {
				return;
			}

			if (isInlineReferenceDirectiveNode(node)) {
				state.addNode(type, { netName });
				return;
			}

			const paragraph = state.schema.nodes['paragraph'];
			if (paragraph === undefined) {
				state.addNode(type, { netName });
				return;
			}

			state.openNode(paragraph).addNode(type, { netName }).closeNode();
		},
	},
	toMarkdown: {
		match: (node) => node.type.name === NET_REFERENCE_NODE_NAME,
		runner: (state, node) => {
			const netName = String(node.attrs[Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName]);
			if (isValidNetReferenceShorthand(netName)) {
				state.addNode('text', undefined, formatNetReferenceMarkdown(netName));
				return;
			}

			state.addNode('textDirective', [{ type: 'text', value: netName }], undefined, {
				name: Markdown.NetReferenceMarkdownDirectiveDefinition.directive,
				attributes: {},
			});
		},
	},
}));

/**
 * Creates the ProseMirror node view that mounts React net chips for net reference atoms.
 */
export function createNetReferenceViewPlugin(controller: ReferenceChipViewController) {
	return $view(netReferenceSchema.node, () =>
		createReferenceChipNodeView(
			NET_REFERENCE_NODE_NAME,
			Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName,
			Markdown.NetReferenceMarkdownDirectiveDefinition.directive,
			controller,
		),
	);
}

/**
 * Returns net reference suggestions matching the current shorthand query.
 */
export function getNetReferenceSuggestions(coreModel: ModelWithNetStats, query: string): MarkdownReferenceSuggestion[] {
	return getNetSearchMatches(coreModel, getNetSearchTerm(query), { includeEmpty: true }).map((netName) =>
		createReferenceSuggestion(Markdown.NetReferenceMarkdownDirectiveDefinition.directive, netName, netName),
	);
}

/**
 * Shared editor plugin descriptor for net references.
 */
export const netReferenceEditorPlugin: MarkdownReferenceEditorPlugin = {
	nodeName: NET_REFERENCE_NODE_NAME,
	kind: Markdown.NetReferenceMarkdownDirectiveDefinition.directive,
	marker: Markdown.NetReferenceMarkdownDirectiveDefinition.editorMarker,
	attrName: Markdown.NetReferenceMarkdownDirectiveDefinition.contentAttributeName,
	suggestionQueryCharClass: 'A-Za-z0-9_./:+-',
	suggestionHint: 'Type to filter nets',
	schema: netReferenceSchema,
	createViewPlugin: createNetReferenceViewPlugin,
	getSuggestions: getNetReferenceSuggestions,
	renderChip: renderNetReferenceChip,
	handleTriggerSpace: convertNetTriggerToHeading,
};

/**
 * Renders a net reference atom as a React chip in the live editor.
 */
function renderNetReferenceChip(value: string) {
	return <NetMarkdownItem netName={value} />;
}

/**
 * Converts a bare `#` net trigger into a level-1 heading when the user presses space, mirroring
 * the markdown heading shorthand that the `#` marker would otherwise shadow.
 *
 * @param view - The editor view.
 * @param trigger - The active net suggestion trigger.
 * @returns True if the trigger was converted to a heading.
 */
function convertNetTriggerToHeading(view: EditorView, trigger: ReferenceSuggestionTrigger): boolean {
	if (trigger.query !== '') {
		return false;
	}

	const { state } = view;
	const heading = state.schema.nodes['heading'];
	const { selection } = state;
	if (
		heading === undefined ||
		!selection.empty ||
		!selection.$from.parent.inlineContent ||
		selection.$from.parentOffset !== 1
	) {
		return false;
	}

	const textBeforeCursor = selection.$from.parent.textBetween(0, selection.$from.parentOffset, undefined, '\uFFFC');
	if (
		textBeforeCursor !== Markdown.NetReferenceMarkdownDirectiveDefinition.editorMarker ||
		trigger.from !== selection.from - 1 ||
		trigger.to !== selection.from
	) {
		return false;
	}

	let transaction = state.tr.delete(trigger.from, trigger.to);
	const blockFrom = transaction.mapping.map(selection.$from.before());
	const blockTo = transaction.mapping.map(selection.$from.after());
	transaction = transaction.setBlockType(blockFrom, blockTo, heading, { level: 1 });
	view.dispatch(transaction.scrollIntoView());
	view.focus();
	return true;
}
